import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { StoreStorageUtil } from '@shared/utils/store-storage-util';
import { Subject } from 'rxjs';

// eslint-disable-next-line import/no-unresolved
import toggle from '../../../../toggle.json';

export const STORE_CODE_CONTEXT_FIELD = 'store_code';

/**
 * หนึ่ง entry ของ toggle.json — `store` ไม่มีแปลว่า flag ใช้กับทุกร้าน ถ้ามีคือจำกัดเฉพาะ store code ในลิสต์
 */
interface DefaultToggle {
  name: string;
  description: string;
  enabled: boolean;
  store?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class UnleashProxyService {
  private unleashService = inject(UnleashService);
  private flags = new Map<string, WritableSignal<boolean>>();
  private defaultToggle: DefaultToggle[] = toggle;
  private updatedSubject = new Subject<void>();
  private currentStoreCode: string | undefined;
  updateTrigger$ = this.updatedSubject.asObservable();

  constructor() {
    this.unleashService.unleash.on('update', () => {
      this.sync();
      this.updatedSubject.next();
    });
  }

  /**
   * บอก Unleash ว่าตอนนี้ทำรายการในนามร้านไหน เพื่อให้ flag ที่ผูก constraint กับร้านถูก evaluate ให้ถูกตัว
   * ต้องเรียกทุกครั้งที่ร้านเปลี่ยนโดยหน้าไม่ได้ reload มิฉะนั้น flag พวกนั้นจะยังตอบค่าของร้านเดิม
   */
  updateStoreContext(storeCode?: string): void {
    const nextStoreCode = storeCode ?? StoreStorageUtil.retrieveStore()?.storeCode;

    if (!nextStoreCode || nextStoreCode === this.currentStoreCode) {
      return;
    }

    this.currentStoreCode = nextStoreCode;
    this.unleashService.unleash.updateContext({
      properties: { [STORE_CODE_CONTEXT_FIELD]: nextStoreCode }
    });
  }

  /**
   * ค่าจาก Unleash ชนะเสมอเมื่อ SDK โหลด toggle มาแล้ว ก่อนหน้านั้นถึงจะตกมาใช้ toggle.json ซึ่งเป็น
   * แหล่งเดียวที่มีก่อน fetch รอบแรก และต้องเคารพลิสต์ `store` ของมันด้วย ไม่งั้น flag ที่จำกัดร้าน
   * จะอ่านเป็นเปิดให้ทุกร้าน
   */
  isEnabledToggle(name: string): boolean {
    if (this.unleashService.unleash.getAllToggles().length > 0) {
      return this.unleashService.isEnabled(name);
    }

    return this.isEnabledFromDefaultToggle(name);
  }

  private isEnabledFromDefaultToggle(name: string): boolean {
    const toggleEntry = this.defaultToggle.find((f) => f.name === name);

    if (!toggleEntry?.enabled) {
      return false;
    }

    if (!toggleEntry.store) {
      return true;
    }

    const storeCode = this.currentStoreCode ?? StoreStorageUtil.retrieveStore()?.storeCode;
    return !!storeCode && toggleEntry.store.includes(storeCode);
  }

  isEnabled$(names: string[]): Signal<boolean> {
    names.forEach((name) => {
      if (!this.flags.has(name)) {
        const initial = this.isEnabledToggle(name);
        this.flags.set(name, signal<boolean>(initial));
      }
    });

    return computed(() => names.some((name) => this.flags.get(name)?.() === true));
  }

  isEnabled(names: string[]): boolean {
    return names.some((name) => this.isEnabledToggle(name));
  }

  private sync() {
    this.flags.forEach((sig, name) => {
      sig.set(this.unleashService.unleash.isEnabled(name));
    });
  }
}
